//import "./safetyforms.css";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import "react-dropdown/style.css";
import { FaChevronLeft, FaChevronRight, FaEnvelope, FaFilter, FaPlus, FaSpinner, FaTimesCircle, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPagination } from '../common/slices/globalActions';
import { findGroups, removeGroup, selectGroups, selectGroupsLoading, selectPagination as selectGroupsPagination } from "../common/slices/group";
import { selectActive } from "../common/slices/location";
import { deleteUser, fetchUserById, searchUsers, selectLoading, selectPagination, selectUser, selectUserById, selectUsers } from "../common/slices/user";
import { pathNames } from "../constants";
import { ErrorContext } from '../ErrorContext';
import capitalizeFirstLetter from "../utils/capitalizeFirstLetter";
import ActionContainer from "./ActionContainer";
import { BlackButton, ButtonBase, LightButton } from "./Buttons";
import Layout from "./Layout";
import ModalConfirm from "./ModalConfirm";
import ModalEditGroup from "./ModalEditGroup";
import ModalEditUser from "./ModalEditUser";
import ModalSendMessage from "./ModalSendMessage";
import Row from "./Row";
import { StyledSearch } from "./Search";
import { MenuItem, Table, TableData, TableDataLast, TableDataUnderline, TableFooter, TableHeader, TableMenu, TableRow } from "./Table";
import { Tab, TabContainer, TabContent } from './Tabs';
import { H1 } from "./Typography";
import { DropdownContainer, DropdownContent } from "./Dropdown";

const ScreenListUsers = () => {
    const locationId = useSelector(selectActive);

    return (
        <Layout>
            <H1 style={{ marginTop: '2rem' }}>Manage Users</H1>
            <TabContainer style={{ gap: '1rem' }} initialTab={0}>
                <Row>
                    <Tab tabId={0}>Users</Tab>
                    <Tab tabId={1}>User Groups</Tab>
                </Row>

                <TabUsersContainer />
                <TabGroupsContainer locationId={locationId} />
            </TabContainer>
        </Layout>
    );
};

const TabUsersContainer = () => (
    <TabContent tabId={0}>
        <TabUsers />
    </TabContent>
)

const TabUsers = () => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const nav = useNavigate();
    const { id } = useParams();
    const users = useSelector(selectUsers);
    const loading = useSelector(selectLoading);
    const pagination = useSelector(selectPagination);
    const user = useSelector(state => selectUserById(state, id));
    const locationId = useSelector(selectActive);
    const [newUserModal, setNewUserModal] = useState(false);
    const [sendMessageModal, setSendMessageModal] = useState(false);
    const [showConfirm, setShowConfirm] = useState(null);
    const [search, setSearch] = useState("");

    useEffect(() => {
        (async () => {
            try {
                if (locationId) {
                    await dispatch(searchUsers(locationId, search));
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to get users from server.", forSupport: ex.message });
            }
        })()
    }, [locationId, search, dispatch]);

    useEffect(() => {
        (async () => {
            try {
                if (id) {
                    await dispatch(fetchUserById(id));
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to get user from server.", forSupport: ex.message });
            }
        })()
    }, [id, dispatch])

    const onUserClick = (d) => {
        nav(`${pathNames.USERS}/${d.id}`);
    };

    const onDeleteUser = async (userId) => {
        try {
            setShowConfirm(null);
            await dispatch(deleteUser({ id: userId }))
            toast.success("Deleted user");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to delete user", forSupport: ex.message });
        }
    }

    const onNextPage = async () => {
        try {
            await dispatch(searchUsers(locationId, search, pagination.nextToken));
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get next page from server.", forSupport: ex.message });
        }
    }

    const onPrevPage = async () => {
        try {
            await dispatch(searchUsers(locationId, search, pagination.previousTokens[pagination.previousTokens.length - 2], false));
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get previous page from server.", forSupport: ex.message });
        }
    }

    return (
        <>
            <ActionContainer>
                <div style={{ flex: 1 }} />
                <StyledSearch onSubmit={({ search }) => setSearch(search)} onResetClick={() => setSearch("")} />
                {/* <LightButton onClick={() => setSendMessageModal(true)}><FaTimesCircle /> Inactive</LightButton> */}
                <LightButton onClick={() => setSendMessageModal(true)}><FaEnvelope /> Send Message</LightButton>
                <LightButton onClick={() => setNewUserModal(true)}><FaPlus /> Create User</LightButton>
            </ActionContainer>
            <Table>
                <thead>
                    <tr>
                        <TableHeader colSpan={2}>Name</TableHeader>
                        <TableHeader style={{ width: "30%" }}>Email</TableHeader>
                        <TableHeader style={{ width: "10%" }}>Role</TableHeader>
                        <TableHeader style={{ width: "30%" }}>Location</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user) => (
                        <TableRow key={user?.id} onClick={() => onUserClick(user)}>
                            <TableData style={{ width: '1rem' }}>{user?.crewMember?.active ? '' : <FaTimesCircle title="Inactive User" />}</TableData>
                            <TableDataUnderline>{`${user?.firstName} ${user?.lastName}`}</TableDataUnderline>
                            <TableData>{user?.email}</TableData>
                            <TableData>{capitalizeFirstLetter(user?.role?.toLowerCase())}</TableData>
                            <TableDataLast>
                                {`${user?.location?.name}`}
                                <TableMenu>
                                    <MenuItem onClick={(e) => { e.stopPropagation(); setShowConfirm(user.id) }}>
                                        Delete
                                        <FaTrash style={{ marginLeft: "auto" }} />
                                    </MenuItem>
                                </TableMenu>
                            </TableDataLast>
                        </TableRow>
                    ))}
                    {
                        !loading && !users.length && <TableRow><TableData colSpan={5}>No users, please adjust your filter</TableData></TableRow>
                    }
                </tbody>
            </Table>
            <TableFooter>
                {loading && <FaSpinner className='fa-spin' />}
                <ButtonBase onClick={onPrevPage} disabled={!pagination.previousTokens[pagination.previousTokens.length - 1] || loading} style={{ color: 'black' }}><FaChevronLeft /></ButtonBase>
                <ButtonBase onClick={onNextPage} disabled={!pagination.nextToken || loading} style={{ color: 'black' }}><FaChevronRight /></ButtonBase>
            </TableFooter>

            {id && <ModalEditUser onClose={() => nav(pathNames.USERS)} user={user} />}
            {newUserModal && <ModalEditUser timesheet={{}} onClose={() => setNewUserModal(false)} />}
            {sendMessageModal && <ModalSendMessage onClose={() => setSendMessageModal(false)} />}
            {showConfirm && <ModalConfirm
                onClose={() => setShowConfirm(null)}
                onConfirm={() => onDeleteUser(showConfirm)}
                title="Confirm Delete"
                description="Are you sure you want to delete this user?  This cannot be undone."
            />}
        </>
    )
}

const TabGroupsContainer = (props) => (
    <TabContent tabId={1}>
        <TabGroups {...props} />
    </TabContent>
)

const TabGroups = ({ locationId }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const [editGroupModal, setEditGroupModal] = useState(false);
    const [showConfirm, setShowConfirm] = useState(null);
    const [activeGroup, setActiveGroup] = useState(null);
    const pagination = useSelector(selectGroupsPagination);
    const loading = useSelector(selectGroupsLoading);
    const groups = useSelector(selectGroups);
    const user = useSelector(selectUser);
    const [search, setSearch] = useState("");

    useEffect(() => {
        (async () => {
            try {
                if (locationId) {
                    dispatch(resetPagination());
                    await dispatch(findGroups(search, locationId, undefined, undefined, !!user.hasDivisionAccess))
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to get groups from server.", forSupport: ex.message });
            }
        })()
    }, [locationId, search, dispatch])

    const onGroupClick = (d) => {
        setActiveGroup(d);
        setEditGroupModal(true);
    };

    const onDeleteGroup = async (id) => {
        setShowConfirm(null);
        await dispatch(removeGroup(id, locationId))
        toast.success("Deleted group");
    }

    const onNextPage = async () => {
        dispatch(findGroups(search, locationId, pagination.nextToken, undefined, !!user.hasDivisionAccess));
    }

    const onPrevPage = async () => {
        dispatch(findGroups(search, locationId, pagination.previousTokens[pagination.previousTokens.length - 2], false, !!user.hasDivisionAccess));
    }

    return (
        <>
            <ActionContainer>
                <div style={{ flex: 1 }} />
                <StyledSearch onSubmit={({ search }) => setSearch(search)} onResetClick={() => setSearch("")} />
                <LightButton onClick={() => setEditGroupModal(true)}><FaPlus /> Create Group</LightButton>
            </ActionContainer>
            <Table>
                <thead>
                    <tr>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>No. of Members</TableHeader>
                        <TableHeader>Created At</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {groups.map((group) => (
                        <TableRow key={group?.id} onClick={() => onGroupClick(group)}>
                            <TableDataUnderline>{group?.name}</TableDataUnderline>
                            <TableData>{group?.crewMemberIds?.length || 0}</TableData>
                            <TableDataLast>
                                {moment(group.createdAt).format("MMM DD, YYYY [at] h:mm a")}
                                <TableMenu>
                                    <MenuItem onClick={(e) => { e.stopPropagation(); setShowConfirm(group.id) }}>
                                        Delete
                                        <FaTrash style={{ marginLeft: "auto" }} />
                                    </MenuItem>
                                </TableMenu>
                            </TableDataLast>
                        </TableRow>
                    ))}
                    {
                        !groups.length && <TableRow><TableData colSpan={5}>No groups, please adjust your filter</TableData></TableRow>
                    }
                </tbody>
            </Table>
            <TableFooter>
                {loading && <FaSpinner className='fa-spin' />}
                <ButtonBase onClick={onPrevPage} disabled={!pagination.previousTokens[pagination.previousTokens.length - 1] || loading} style={{ color: 'black' }}><FaChevronLeft /></ButtonBase>
                <ButtonBase onClick={onNextPage} disabled={!pagination.nextToken || loading} style={{ color: 'black' }}><FaChevronRight /></ButtonBase>
            </TableFooter>

            {editGroupModal && <ModalEditGroup group={activeGroup} onClose={() => {
                setActiveGroup(null);
                setEditGroupModal(false)
            }} />}
            {showConfirm && <ModalConfirm
                onClose={() => setShowConfirm(null)}
                onConfirm={() => onDeleteGroup(showConfirm)}
                title="Confirm Delete"
                description="Are you sure you want to delete this group?  This cannot be undone."
            />}
        </>
    )
}


export default ScreenListUsers;
